// Localização: frontend/src/components/Pacientes/Prontuario/PacienteOverviewTab.jsx (0.2.1)

import React from 'react';
import { usePacienteProntuario } from '../../hooks/data/usePacienteProntuario';
import Title from '../Layout/Title';
import Card from '../UI/Card';
import Alert from '../UI/Alert'; 


import GraficoLinhaSinaisVitais from '../../components/Charts/GraficoLinhaSinaisVitais'; 


const PacienteOverviewTab = ({ pacienteId }) => {
    const { data: summaryData, isLoading: isSummaryLoading, error: summaryError } = usePacienteProntuario(pacienteId);

    // 1. CHECAGEM DE LOADING
    if (isSummaryLoading) {
        // Usamos as classes de padding e margem para garantir que a mensagem de loading não fique "colada"
        return <p className="py-6 text-center text-emerald-600">Carregando dados da Visão Geral...</p>;
    }
    
    // 2. CHECAGEM DE ERRO/DADOS AUSENTES
    if (summaryError || !summaryData) { 
        return (
            <Alert 
                type="error" 
                message={summaryError || "Não foi possível carregar os dados do resumo do prontuário."} 
            />
        );
    }
    
    // Alertas Críticos de Validade
    const AlertasCriticosList = () => (
        <Card title="Alertas de Medicação Crítica (Paciente)" className="p-4 md:p-6"> 
            {summaryData.alertasMedicacao && summaryData.alertasMedicacao.length > 0 ? (
                <ul className="divide-y divide-red-100 space-y-0">
                    {summaryData.alertasMedicacao.map((alerta, index) => (
                        <li key={index} className="py-3 text-sm first:pt-0 last:pb-0">
                            <span className="font-semibold text-red-600">{alerta.nome_medicamento}:</span> Lote **{alerta.numero_lote}**
                            {/* 💡 Quebra de Linha Opcional (apenas para telas menores) */}
                            <br className="sm:hidden" /> 
                            <span className="ml-2 text-gray-500">vence em {alerta.data_validade}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-green-600 py-2">Nenhum lote crítico de medicação encontrado para este paciente.</p>
            )}
        </Card>
    );

    const { paciente } = summaryData;
    
    // Função auxiliar para formatar dados
    const formatField = (value) => value || 'Não informado';
    
    return (
        // Espaçamento vertical melhorado com padding no container
        <div className="space-y-6 py-4 px-2 md:px-4"> 
            <Title text="Visão Geral do Prontuário" size="small" />

            {/* Seção: IDENTIFICAÇÃO DO PACIENTE */}
            <Card className="p-0 overflow-hidden">
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 px-4 py-2 border-b border-emerald-200">
                    <h3 className="text-sm font-semibold text-gray-800 text-center">IDENTIFICAÇÃO DO PACIENTE</h3>
                </div>
                <div className="p-4 space-y-4">
                    {/* Linha 1: Paciente e RG */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                        <div className="sm:col-span-10">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Paciente:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.nome_completo)}</p>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">RG:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.rg)}</p>
                        </div>
                    </div>

                    {/* Linha 2: CPF, NIT/PIS, Cartão SUS */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CPF:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.cpf)}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">NIT/PIS:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.nit_pis)}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cartão SUS:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.cartao_sus)}</p>
                        </div>
                    </div>

                    {/* Linha 3: NB */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">NB:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.numero_beneficio)}</p>
                        </div>
                    </div>

                    {/* Linha 4: Endereço Residencial e Bairro */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                        <div className="sm:col-span-10">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Endereço Residencial:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.endereco)}</p>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bairro:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.bairro)}</p>
                        </div>
                    </div>

                    {/* Linha 5: Cidade, Estado, CEP */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.cidade)}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1 uppercase">{formatField(paciente.estado)}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CEP:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.cep)}</p>
                        </div>
                    </div>

                    {/* Linha 6: Telefone Residencial e Celular */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone Residencial:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.telefone_residencial)}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Celular:</label>
                            <p className="text-sm text-gray-900 border-b-2 border-gray-300 pb-1">{formatField(paciente.celular)}</p>
                        </div>
                    </div>
                </div>
            </Card>

            <Card title="Tendência de Sinais Vitais (Últimos 30 dias)" className="p-4 md:p-6">
                {/* Container com padding adicional para o gráfico */}
                <div className="pt-2">
                    <GraficoLinhaSinaisVitais pacienteId={pacienteId} />
                </div>
            </Card>

            <AlertasCriticosList />

            <Card title="Informações Adicionais / Notas Recentes" className="p-4 md:p-6">
                <p className="text-sm text-gray-600">
                    Última atualização de Condição Médica: {summaryData.paciente.condicao_medica || 'Não informado'}
                </p>
            </Card>
        </div>
    );
};

export default PacienteOverviewTab;